import React from 'react';
import { useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

import { addModal } from 'actions/modalActions';
import {
  getFastedForTimeValue,
  getFastedRemainingTime,
  isFastingGoalReached,
} from 'helpers/fastingHelper';
import { ModalType } from 'types/modals';
import { Cycle, FastingTimerFormat, Timer } from 'types/fasting';

import { useFastingTimer } from 'hooks';

import { HorseShoeEating } from 'components/ProgressBar';
import { SubmitButton, SvgIcon } from 'components/common';

import s from './EatingTimerBody.module.scss';

type Props = {
  cycle: Cycle;
  timer: Timer;
};

function EatingTimerBody({ cycle, timer }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { activeSecondsInFasting } = useFastingTimer({ timer });
  const fastingGoalReached = isFastingGoalReached(
    cycle.eating,
    timer.time_in_the_cycle
  );
  const humanReadibleTimer = getFastedForTimeValue(0, activeSecondsInFasting);

  const getTitle = () => {
    if (fastingGoalReached) {
      return t('items.type.eating.done_timer.title');
    }

    return t('items.type.eating.in_fasting_timer.title');
  };

  const getText = () => {
    if (fastingGoalReached) {
      return (
        <Trans i18nKey="items.type.eating.done_timer.text">
          <SvgIcon filename="FastingCup" />
          <span className={s.congratz}>Great!</span>
          {{ goal_time: cycle.eating }}h of eating time is done and you can
          start fasting!!
        </Trans>
      );
    }

    return (
      <Trans i18nKey="items.type.eating.in_fasting_timer.text">
        Remaining{' '}
        <span className={s.semiBold}>
          {{
            remaining_time: getFastedRemainingTime(
              cycle.eating,
              activeSecondsInFasting,
              FastingTimerFormat.medium
            ),
          }}
        </span>
      </Trans>
    );
  };

  const getButtonText = () => {
    if (fastingGoalReached) {
      return t('items.type.eating.done_timer.button');
    }

    return t('items.type.eating.in_fasting_timer.button');
  };

  const handleButton = (e: any) => {
    e.preventDefault();

    dispatch(
      addModal({
        modal: ModalType.stopEating,
        progressTime: getFastedForTimeValue(
          0,
          activeSecondsInFasting,
          FastingTimerFormat.medium
        ),
        remainingTime: getFastedRemainingTime(
          cycle.eating,
          activeSecondsInFasting,
          FastingTimerFormat.medium
        ),
        fastingHours: cycle.fasting,
      })
    );
  };

  return (
    <div className={`${s.cardContainer} ${fastingGoalReached && s.doneEating}`}>
      <div className={s.cardWrapper}>
        <div className={s.leftSide}>
          <span className={s.cardTitle}>{getTitle()}</span>
          <p className={s.cardTimer}>{humanReadibleTimer}</p>
          <p className={s.cardText}>{getText()}</p>
        </div>
        <div className={s.rightSide}>
          <HorseShoeEating cycle={cycle} timer={timer} />
        </div>
      </div>
      <div className={`${s.button} ${fastingGoalReached && s.doneEating}`}>
        <SubmitButton
          label={getButtonText()}
          type="large"
          onClick={(e) => handleButton(e)}
        />
      </div>
    </div>
  );
}

export default EatingTimerBody;
